import React, { useState, useEffect } from 'react';
import './SourceManager.css';

function SourceManager() {
  const [sources, setSources] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [processing, setProcessing] = useState(false);

  const API_BASE = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    fetchSourcesAndStats();
  }, []);

  const fetchSourcesAndStats = async () => {
    setLoading(true);
    setError(null);

    try {
      const [sourcesRes, statsRes] = await Promise.all([
        fetch(`${API_BASE}/api/sources`),
        fetch(`${API_BASE}/api/stats`)
      ]);

      const sourcesData = await sourcesRes.json();
      const statsData = await statsRes.json();

      if (sourcesData.success) {
        setSources(sourcesData.sources);
      }

      if (statsData.success) {
        setStats(statsData.stats);
      }

    } catch (err) {
      setError('Failed to load sources: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const processPendingSources = async () => {
    if (!window.confirm('This will start processing all pending sources. Continue?')) {
      return;
    }

    setProcessing(true);
    try {
      const response = await fetch(`${API_BASE}/api/sources/process-pending`, {
        method: 'POST'
      });

      const data = await response.json();

      if (data.success) {
        alert(`Started processing ${data.count} pending sources. Check back later for results.`);
        fetchSourcesAndStats();
      } else {
        alert('Failed to start processing: ' + data.error);
      }

    } catch (err) {
      alert('Failed to start processing: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  const filteredSources = sources.filter(source => {
    if (filterCategory !== 'all' && source.category !== filterCategory) {
      return false;
    }
    if (filterStatus !== 'all' && source.status !== filterStatus) {
      return false;
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesName = source.name?.toLowerCase().includes(query);
      const matchesShortName = source.short_name?.toLowerCase().includes(query);
      const matchesCategory = source.category?.toLowerCase().includes(query);
      const matchesType = source.type?.toLowerCase().includes(query);
      if (!matchesName && !matchesShortName && !matchesCategory && !matchesType) {
        return false;
      }
    }
    return true;
  });

  const categories = [...new Set(sources.map(s => s.category))].sort();
  const statuses = ['pending', 'collecting', 'processing', 'completed', 'error'];

  const getStatusColor = (status) => {
    const colors = {
      pending: '#cbd5e1',
      collecting: '#93c5fd',
      processing: '#fbbf24',
      completed: '#34d399',
      error: '#f87171'
    };
    return colors[status] || '#cbd5e1';
  };

  if (loading) {
    return (
      <div className="source-manager">
        <div className="loading">Loading sources...</div>
      </div>
    );
  }

  return (
    <div className="source-manager">
      <div className="source-header">
        <h1>Legal Source Manager</h1>
        <p className="subtitle">Manage and track legal documents for RAG system</p>
      </div>

      {/* Statistics Dashboard */}
      {stats && (
        <div className="stats-dashboard">
          <div className="stat-card">
            <div className="stat-value">{stats.totalSources}</div>
            <div className="stat-label">Total Sources</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.totalDocuments}</div>
            <div className="stat-label">Documents</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.totalEmbeddings}</div>
            <div className="stat-label">Embeddings</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">
              {stats.totalEmbeddings > 0
                ? Math.round((stats.totalEmbeddings / stats.totalSources) * 100) / 100
                : 0}
            </div>
            <div className="stat-label">Avg per Source</div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="filters">
        <div className="filter-group" style={{ flex: 2 }}>
          <label>Search:</label>
          <input
            type="text"
            placeholder="Search by name, category, or type..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <label>Category:</label>
          <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Status:</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Statuses</option>
            {statuses.map(status => (
              <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <button onClick={fetchSourcesAndStats} className="refresh-button">
            🔄 Refresh
          </button>
        </div>

        <div className="filter-group">
          <button
            onClick={processPendingSources}
            className="process-button"
            disabled={processing}
          >
            {processing ? '⏳ Processing...' : '▶️ Process Pending'}
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Sources Table */}
      <div className="sources-table-container">
        {/* Record Count */}
        <div style={{
          marginBottom: '12px',
          fontSize: '14px',
          color: '#64748b',
          fontWeight: '500'
        }}>
          {filteredSources.length === sources.length ? (
            <span>Showing {sources.length} {sources.length === 1 ? 'source' : 'sources'}</span>
          ) : (
            <span>Showing {filteredSources.length} of {sources.length} sources</span>
          )}
        </div>

        <table className="sources-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Category</th>
              <th>Type</th>
              <th>Year</th>
              <th>Status</th>
              <th>URL</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSources.map(source => (
              <tr key={source.id}>
                <td>
                  <div className="source-name">{source.short_name || source.name}</div>
                  {source.short_name && (
                    <div className="source-full-name">{source.name}</div>
                  )}
                </td>
                <td>
                  <span className="category-badge">{source.category}</span>
                </td>
                <td>{source.type}</td>
                <td>{source.year}</td>
                <td>
                  <span
                    className="status-badge"
                    style={{
                      backgroundColor: getStatusColor(source.status),
                      color: '#fff',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      fontSize: '13px',
                      fontWeight: '500'
                    }}
                  >
                    {source.status.charAt(0).toUpperCase() + source.status.slice(1)}
                  </span>
                </td>
                <td>
                  {source.url && (
                    <a href={source.url} target="_blank" rel="noopener noreferrer" className="url-link">
                      View Source
                    </a>
                  )}
                </td>
                <td>
                  <button
                    className="action-button"
                    onClick={() => window.open(`/api/sources/${source.id}/documents`, '_blank')}
                  >
                    View Docs
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredSources.length === 0 && (
          <div className="no-results">
            No sources found matching your filters.
          </div>
        )}
      </div>

      {/* Category Breakdown */}
      {stats && stats.byCategory && stats.byCategory.length > 0 && (
        <div className="category-breakdown">
          <h3>Sources by Category</h3>
          <div className="category-grid">
            {stats.byCategory.map(cat => (
              <div key={cat.category} className="category-item">
                <div className="category-count">{cat.count}</div>
                <div className="category-name">{cat.category}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="instructions">
        <h3>Next Steps</h3>
        <ol>
          <li>
            <strong>Initialize Database:</strong> Run <code>npm run init-db</code> to set up the database and load all {sources.length} legal sources
          </li>
          <li>
            <strong>Collect Legal Texts:</strong> Use web scrapers or APIs to fetch the actual text for each law
          </li>
          <li>
            <strong>Generate Embeddings:</strong> Process documents through Fireworks.ai embeddings API
          </li>
          <li>
            <strong>Query RAG System:</strong> Use the <code>/api/rag/query</code> endpoint to ask questions about laws
          </li>
        </ol>
      </div>
    </div>
  );
}

export default SourceManager;
